import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';

import { catalogApi } from '@/api';
import { Field, Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

const SUGGESTION_LIMIT = 8;
const DEBOUNCE_MS = 200;

const same = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Skills as chips, with suggestions from the skill catalogue as you type.
 *
 * Picking a suggestion keeps names consistent with what students list, which
 * is what matching compares. A name that is not in the catalogue can still be
 * added (Enter or a comma): the server resolves aliases and creates the rest,
 * exactly as it did for the old comma-separated box.
 *
 * `value` is an array of skill names; `onChange` receives the new array.
 */
export function SkillsInput({ label = 'Skills', hint, value, onChange, placeholder }) {
  const inputId = useId();
  const listId = useId();
  const inputRef = useRef(null);
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Ask the catalogue only once typing pauses.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const { data, isFetching } = useQuery({
    queryKey: ['skills', 'suggest', term],
    queryFn: () => catalogApi.listSkills(term, SUGGESTION_LIMIT + value.length),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const typed = text.trim();
  const needle = typed.toLowerCase();
  // Filtered and ordered against what is typed *now*, not what the last
  // request asked for: results lag the keyboard by the debounce, and Enter
  // must never pick a stale suggestion that no longer matches the text.
  const rank = (name) => {
    const lower = name.toLowerCase();
    if (lower === needle) return 0;
    return lower.startsWith(needle) ? 1 : 2;
  };
  const suggestions = (data ?? [])
    .filter((skill) => !value.some((chosen) => same(chosen, skill.name)))
    .filter((skill) => skill.name.toLowerCase().includes(needle))
    .sort((a, b) => rank(a.name) - rank(b.name))
    .slice(0, SUGGESTION_LIMIT);
  // Offer the typed text itself when the catalogue has no exact match.
  const offerTyped =
    typed.length > 0 &&
    !value.some((chosen) => same(chosen, typed)) &&
    !suggestions.some((skill) => same(skill.name, typed));
  const options = [
    ...suggestions.map((skill) => ({ key: skill.id, name: skill.name, isNew: false })),
    ...(offerTyped ? [{ key: `new:${typed}`, name: typed, isNew: true }] : []),
  ];
  const active = Math.min(highlight, Math.max(options.length - 1, 0));

  function add(name) {
    const clean = name.trim();
    if (clean && !value.some((chosen) => same(chosen, clean))) onChange([...value, clean]);
    setText('');
    setHighlight(0);
    inputRef.current?.focus();
  }

  function remove(name) {
    onChange(value.filter((chosen) => chosen !== name));
    inputRef.current?.focus();
  }

  function onKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((index) => (options.length ? (index + 1) % options.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((index) => (options.length ? (index - 1 + options.length) % options.length : 0));
    } else if (event.key === 'Enter') {
      // Never submit the whole form from here.
      event.preventDefault();
      if (open && options[active]) add(options[active].name);
      else if (typed) add(typed);
    } else if (event.key === ',' || event.key === 'Tab') {
      if (typed) {
        event.preventDefault();
        add(typed);
      }
    } else if (event.key === 'Backspace' && text === '' && value.length > 0) {
      remove(value[value.length - 1]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // Pasting "Python, SQL, Docker" still works: every complete name is added.
  function onTextChange(next) {
    if (next.includes(',')) {
      const parts = next.split(',');
      const rest = parts.pop();
      const names = parts.map((part) => part.trim()).filter(Boolean);
      const merged = [...value];
      for (const name of names) {
        if (!merged.some((chosen) => same(chosen, name))) merged.push(name);
      }
      if (merged.length !== value.length) onChange(merged);
      setText(rest.trimStart());
    } else {
      setText(next);
    }
    setOpen(true);
    setHighlight(0);
  }

  const showList = open && (options.length > 0 || isFetching);

  return (
    <Field label={label} htmlFor={inputId} hint={hint}>
      <div className="relative">
        <div
          className={cn(
            'border-line bg-surface rounded-input flex min-h-9 w-full flex-wrap items-center gap-1.5 border px-2 py-1 shadow-xs transition',
            'hover:border-brand-300 focus-within:border-brand-500 focus-within:ring-brand-100 focus-within:ring-2',
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {value.map((name) => (
            <span
              key={name}
              className="bg-brand-50 text-brand-700 ring-brand-100 inline-flex items-center gap-1 rounded-md py-0.5 pr-1 pl-2 text-[13px] font-medium ring-1 ring-inset"
            >
              {name}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  remove(name);
                }}
                className="hover:bg-brand-100 grid size-4 place-items-center rounded"
                aria-label={`Remove ${name}`}
              >
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && options[active] ? `${listId}-${active}` : undefined}
            autoComplete="off"
            value={text}
            placeholder={value.length === 0 ? placeholder : 'Add another skill'}
            onChange={(event) => onTextChange(event.target.value)}
            onFocus={() => setOpen(true)}
            // Closing waits a beat so a click on a suggestion lands first.
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={onKeyDown}
            className="text-ink placeholder:text-ink-muted/70 min-w-[10rem] flex-1 bg-transparent py-0.5 text-sm outline-none"
          />
        </div>

        {showList && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Skill suggestions"
            className="border-line bg-surface absolute inset-x-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border p-1 shadow-lg"
          >
            {options.length === 0 ? (
              <li className="text-ink-muted px-2.5 py-2 text-sm">Searching…</li>
            ) : (
              options.map((option, index) => (
                <li
                  key={option.key}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  // mousedown, not click: it fires before the input blurs.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    add(option.name);
                  }}
                  onMouseEnter={() => setHighlight(index)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm',
                    index === active ? 'bg-brand-50 text-brand-700' : 'text-ink',
                  )}
                >
                  <Icon
                    name={option.isNew ? 'plus' : 'skill'}
                    size={14}
                    className="text-ink-muted shrink-0"
                  />
                  {option.isNew ? (
                    <span>
                      Add <span className="font-medium">“{option.name}”</span>
                    </span>
                  ) : (
                    option.name
                  )}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </Field>
  );
}
